import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppState, store } from '../store';
import { clearFcmToken } from '../store/slices/FcmSlice';
import { deleteToken, onMessage } from 'firebase/messaging';
import { messaging } from '../config/firebase';
import { request } from '../Utils/request';
import { getPersistentDeviceId } from '../Utils/deviceId';
import { NOTIFICATION_ICON_URL } from '../constants/notificationIcon';

const useFirebaseMessaging = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state: AppState) => state.auth.isLoggedIn);

  useEffect(() => {
    if (!isLoggedIn) {
      console.log('[FCM] init effect: user logged out, skipping');
      return; // do nothing while logged out
    }
    if (!messaging) {
      return; // messaging not available
    }
    if (!('serviceWorker' in navigator)) {
      return;
    }

    const normalizeDeepLinkPath = (path: string): string => {
      const safePath = path.startsWith('/') ? path : `/${path}`;
      let parsed: URL;
      try {
        parsed = new URL(safePath, window.location.origin);
      } catch {
        return safePath;
      }

      const parts = parsed.pathname.split('/').filter(Boolean);
      const resource = (parts[0] || '').toLowerCase();
      const id = parts[1];

      let mappedPath = parsed.pathname;
      if (resource === 'post' || resource === 'posts') {
        mappedPath = id ? `/post/${id}` : '/post';
      } else if (resource === 'place' || resource === 'places') {
        mappedPath = id ? `/places/${id}` : '/allplaces';
      } else if (resource === 'trip' || resource === 'trips') {
        mappedPath = id ? `/trips/${id}` : '/trips';
      } else if (resource === 'itinerary' || resource === 'itineraries') {
        mappedPath = id ? `/trips/${id}` : '/trips';
        if (!parsed.searchParams.has('type')) {
          parsed.searchParams.set('type', 'itinerary');
        }
      }

      const query = parsed.searchParams.toString();
      return `${mappedPath}${query ? `?${query}` : ''}${parsed.hash}`;
    };

    const getDeepLinkPath = (payload: any): string | null => {
      const data = payload?.data || {};
      const notification = payload?.notification || {};
      const rawLink =
        data.deepLink ||
        data.deeplink ||
        data.link ||
        notification.click_action ||
        '';

      if (!rawLink) return null;
      const link = String(rawLink).trim();
      if (!link) return null;

      if (link.startsWith('http://') || link.startsWith('https://')) {
        try {
          const url = new URL(link);
          return normalizeDeepLinkPath(`${url.pathname}${url.search}${url.hash}`);
        } catch {
          return null;
        }
      }

      return normalizeDeepLinkPath(link.startsWith('/') ? link : `/${link}`);
    };

    const navigateToDeepLink = (path: string | null) => {
      if (!path) return;
      window.location.assign(path);
    };

    const unsubscribe = onMessage(messaging, (payload) => {
      const deepLinkPath = getDeepLinkPath(payload);
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        const { title = 'Notification', body = '', icon } = (payload.notification as any) || {};
        const browserNotification = new Notification(title, { body, icon: icon || NOTIFICATION_ICON_URL });
        browserNotification.onclick = () => {
          window.focus();
          navigateToDeepLink(deepLinkPath);
          browserNotification.close();
        };
        window.dispatchEvent(
          new CustomEvent('app-toast', {
            detail: {
              avatar: NOTIFICATION_ICON_URL,
              title: title,
              message: body,
              type: 'info',
              onClick: () => navigateToDeepLink(deepLinkPath),
            },
          }),
        );
      } else if (!(window as any).__notificationMsgShown) {
        (window as any).__notificationMsgShown = true;
        window.dispatchEvent(
          new CustomEvent('app-toast', {
            detail: {
              avatar: NOTIFICATION_ICON_URL,
              message: 'Enable notifications from browser settings',
              type: 'warning',
              duration: 5000,
            },
          }),
        );
      }
    });

    navigator.serviceWorker.register('/firebase-messaging-sw.js').catch((err) => console.error('[FCM] SW registration error:', err));

    return () => {
      unsubscribe();
    };
  }, [isLoggedIn]);

  // Clear token on logout
  useEffect(() => {
    if (isLoggedIn) {
      return;
    }
    if (!messaging) {
      return; // messaging not available
    }

    (async () => {
      try {
        const currentTokenDeleted = await deleteToken(messaging);

        if (currentTokenDeleted) {
          console.info('[FCM] Token deleted locally');
        }
      } catch (err) {
        console.error('[FCM] Failed to delete token', err);
      } finally {
        dispatch(clearFcmToken());
      }

      // Optionally inform backend
      const deviceId = getPersistentDeviceId();
      const currentState = store.getState();
      const authToken = currentState?.auth?.token;
      if (!authToken) {
        return;
      }

      try {
        await request<unknown, { deviceId: string }>('/profile/fcm/remove-token', {
          method: 'POST',
          authorization: true,
          body: {
            deviceId: deviceId,
          },
        }).then((res: any) => {
          console.info('[FCM] Token removed on backend', res);
        }).catch((err: any) => {
          console.error('[FCM] Failed to remove token on backend', err);
        });
        console.info('[FCM] Token removed on backend');
      } catch (err) {
        console.error('[FCM] Failed to remove token on backend', err);
      }
    })();
  }, [isLoggedIn, dispatch]);
};

export default useFirebaseMessaging; 